import { Direction } from "odyssey-shared";
import type { SceneDirector } from "./scene-director.ts";
import { panPoint, type BenchRoute, type RouteStep, type WalkWindow } from "./route.ts";
import { chunkKey, newRowLog, rowFigures, type RowFigures, type RowLog } from "./stats.ts";

/**
 * Runs a benchmark route on the game page (P2.0-T4): steers the camera and the character through the route's steps as
 * the scene's director, and records every frame of each measured span into its row, with the chunk the camera's
 * centre stood in and the moments the loaded chunk range changed. It reads the page through `RunnerSources` alone, so
 * it runs on any map a route names and is driven by hand in tests. Its clock is the caller's; the route's speeds are
 * in time, not frames, so a slow machine covers the same ground as a fast one.
 */

/**
 * What the runner reads from the page each frame.
 */
export interface RunnerSources {
  readonly tileSize: number;
  readonly chunkSize: number;
  now(): number;
  cameraCentre(): { readonly x: number; readonly y: number };
  painted(): { readonly painted: boolean; readonly minCx: number; readonly minCy: number; readonly maxCx: number; readonly maxCy: number };
  character(): { readonly x: number; readonly y: number };
  props(): number;
  creatures(): number;
}

/**
 * The directions a walk step names, as the input carries them.
 */
const WALK: Readonly<Record<Extract<RouteStep, { kind: "walk" }>["direction"], Direction>> = { left: Direction.Left, right: Direction.Right, up: Direction.Up, down: Direction.Down };

/**
 * Frames between creature counts, which build a list when read.
 */
const CREATURE_EVERY = 15;

/**
 * Where the runner stands.
 */
export type RunnerState = "idle" | "running" | "done";

/**
 * The runner.
 */
export class BenchRunner implements SceneDirector {
  private route: BenchRoute | null = null;
  private stepIndex = -1;
  private stepStarted = 0;
  private lastFrame = -1;
  private lastRange = "";
  private frameCount = 0;
  private walkLast = { at: Number.NaN, since: 0 };
  private readonly logs: { id: string; log: RowLog; note: string }[] = [];
  private current: { id: string; log: RowLog; note: string } | null = null;
  private readonly windows = new Map<WalkWindow, { id: string; log: RowLog; note: string }>();
  private runState: RunnerState = "idle";

  /**
   * @param sources What the runner reads from the page.
   * @param onDone Called once with every row when the route ends.
   */
  public constructor(
    private readonly sources: RunnerSources,
    private readonly onDone: (rows: readonly RowFigures[], logs: readonly RowLog[]) => void,
  ) {}

  /**
   * Where the runner stands.
   *
   * @returns The state.
   */
  public get state(): RunnerState {
    return this.runState;
  }

  /**
   * The id of the step running, or "" outside a run.
   *
   * @returns The id.
   */
  public get stepId(): string {
    return this.route?.steps[this.stepIndex]?.id ?? "";
  }

  /**
   * Starts a route from its first step.
   *
   * @param route The route.
   * @throws Error when a run is already going.
   */
  public start(route: BenchRoute): void {
    if (this.runState === "running") {
      throw new Error("a benchmark run is already going");
    }
    this.route = route;
    this.logs.length = 0;
    this.runState = "running";
    this.enter(0);
  }

  /**
   * Enters a step, or ends the run past the last.
   *
   * @param index The step's index.
   */
  private enter(index: number): void {
    const route = this.route as BenchRoute;
    this.closeRows();
    this.stepIndex = index;
    this.stepStarted = this.sources.now();
    this.lastFrame = -1;
    this.walkLast = { at: Number.NaN, since: this.stepStarted };
    const step = route.steps[index];
    if (step === undefined) {
      this.runState = "done";
      this.onDone(
        this.logs.map((row) => rowFigures(row.id, row.log, row.note)),
        this.logs.map((row) => row.log),
      );
      return;
    }
    if (step.kind !== "walk") {
      this.current = { id: step.id, log: newRowLog(), note: step.note };
    } else {
      for (const window of step.windows) {
        this.windows.set(window, { id: window.id, log: newRowLog(), note: window.note });
      }
    }
  }

  /**
   * Files the rows of the step being left, in route order.
   */
  private closeRows(): void {
    if (this.current !== null) {
      this.logs.push(this.current);
      this.current = null;
    }
    for (const row of this.windows.values()) {
      this.logs.push(row);
    }
    this.windows.clear();
  }

  /**
   * Where the camera centres, in world pixels: a pan's point along its waypoints, else the character.
   *
   * @returns The point, or null.
   */
  public focus(): { readonly x: number; readonly y: number } | null {
    const step = this.runState === "running" ? this.route?.steps[this.stepIndex] : undefined;
    if (step?.kind !== "pan") {
      return null;
    }
    const at = panPoint(step.waypoints, this.sources.now() - this.stepStarted - step.settleMs, step.measureMs);
    return { x: at.x * this.sources.tileSize, y: at.y * this.sources.tileSize };
  }

  /**
   * The walk's direction during a walk step, else none during a run, else the keys.
   *
   * @returns The direction, or null.
   */
  public intent(): Direction | null {
    if (this.runState !== "running") {
      return null;
    }
    const step = this.route?.steps[this.stepIndex];
    return step?.kind === "walk" ? WALK[step.direction] : Direction.None;
  }

  /**
   * Records one drawn frame and moves the route on: call once after every frame is drawn.
   */
  public frame(): void {
    if (this.runState !== "running") {
      return;
    }
    const step = (this.route as BenchRoute).steps[this.stepIndex] as RouteStep;
    const now = this.sources.now();
    const elapsed = now - this.stepStarted;
    const range = this.sources.painted();
    const rangeKey = range.painted ? `${range.minCx},${range.minCy},${range.maxCx},${range.maxCy}` : "";
    const crossed = rangeKey !== this.lastRange;
    this.lastRange = rangeKey;
    this.frameCount += 1;
    let row: RowLog | null = null;
    if (step.kind === "hold") {
      row = this.current?.log ?? null;
      if (elapsed >= step.measureMs) {
        this.record(row, now, crossed);
        this.enter(this.stepIndex + 1);
        return;
      }
    } else if (step.kind === "pan") {
      if (elapsed < step.settleMs) {
        this.lastFrame = -1;
        return;
      }
      row = this.current?.log ?? null;
      if (elapsed >= step.settleMs + step.measureMs) {
        this.record(row, now, crossed);
        this.enter(this.stepIndex + 1);
        return;
      }
    } else {
      const character = this.sources.character();
      const along = step.direction === "left" || step.direction === "right" ? character.x : character.y;
      if (along !== this.walkLast.at) {
        this.walkLast = { at: along, since: now };
      }
      const window = step.windows.find((candidate) => along >= candidate.from && along < candidate.to);
      row = window === undefined ? null : (this.windows.get(window)?.log ?? null);
      const passed = step.direction === "right" || step.direction === "down" ? along >= step.until : along <= step.until;
      if (passed || now - this.walkLast.since >= step.stuckMs) {
        if (!passed) {
          for (const pending of this.windows.values()) {
            pending.note = `${pending.note}; the walk stopped at ${Math.round(along)} after ${step.stuckMs} ms without moving`.replace(/^; /, "");
          }
        }
        this.enter(this.stepIndex + 1);
        return;
      }
    }
    this.record(row, now, crossed);
  }

  /**
   * Adds the frame to a row: its time since the last frame recorded, its end, the camera's chunk, a crossing when the
   * loaded range changed, and the props and creatures drawn.
   *
   * @param row The row, or null between rows.
   * @param now The frame's end.
   * @param crossed Whether the loaded chunk range changed on this frame.
   */
  private record(row: RowLog | null, now: number, crossed: boolean): void {
    if (row === null) {
      this.lastFrame = -1;
      return;
    }
    if (this.lastFrame >= 0) {
      const centre = this.sources.cameraCentre();
      const pixels = this.sources.tileSize * this.sources.chunkSize;
      row.frameMs.push(now - this.lastFrame);
      row.endedAt.push(now);
      row.chunks.push(chunkKey(Math.max(0, Math.floor(centre.x / pixels)), Math.max(0, Math.floor(centre.y / pixels))));
      if (crossed) {
        row.crossingsAt.push(now);
      }
    }
    this.lastFrame = now;
    row.props = Math.max(row.props, this.sources.props());
    if (this.frameCount % CREATURE_EVERY === 0) {
      row.creatures = Math.max(row.creatures, this.sources.creatures());
    }
  }
}
